import type { PersonSummary } from './registry'

// Pre-loading rewards (drink tickets and/or points) for someone who has no
// BRE account yet, and the grouped view of who holds Hub access. Shared by
// core, the web Worker and the UI.

// What BRE Auth said about a subject when Hub asked for one by email or
// phone, stored with when. Hub asks again only for a held pre-load (see
// PendingPreload) and when someone pre-loads the same contact again.
//   existing_account     a signed-in account that proved this email (or
//                        this phone, by SMS code)
//   unverified_account   (email only) a signed-in account holds this email
//                        but never proved it. Hub grants nothing to it until
//                        it is proven: the pre-load is held.
//   placeholder_*        an unclaimed placeholder Auth had, or just made
export const AUTH_ACCOUNT_STATUSES = ['existing_account', 'unverified_account', 'placeholder_existing', 'placeholder_created'] as const
export type AuthAccountStatus = (typeof AUTH_ACCOUNT_STATUSES)[number]

export const isAuthAccountStatus = (value: unknown): value is AuthAccountStatus => typeof value === 'string' && (AUTH_ACCOUNT_STATUSES as readonly string[]).includes(value)
export const isPlaceholderStatus = (value: AuthAccountStatus | null | undefined): boolean => value === 'placeholder_existing' || value === 'placeholder_created'

// BRE Auth's internal Hub surface (bre-auth src/routes/hub-internal.js,
// contract as of bre-auth 38e09a3). Hub sends BRE_AUTH_HUB_SECRET as a
// bearer; Auth holds it as HUB_AUTH_SECRET. Errors are {error: "<code>",
// message}; 401 unauthorized, 503 not_configured.
//   POST /internal/hub/v1/placeholders  {email | phone (10-digit US), name?, actorPersonId?}
//     201 {sub, status: 'placeholder_created'}
//     200 {sub, status: 'placeholder_existing' | 'existing_account' | 'unverified_account'}
//     400 invalid_request | invalid_email | invalid_phone
//     A phone nobody proved by SMS always gets a placeholder, never an
//     account that merely typed it. A deleted account's email gets a new sub.
//   POST /internal/hub/v1/invitations   {sub, sentByName? (<=60), rewardsText? (<=80), actorPersonId?}
//     200 {sent: true, kind: 'claim' | 'confirm', expiresAt}: `claim` for an
//       email placeholder, `confirm` ("Confirm your BRE account", which only
//       proves the email) for an unverified_account. Auth before 38e09a3
//       sends no kind, which means claim.
//     404 not_found; 409 existing_account (set up, email proven); 422 no_email;
//     429 rate_limited (3 a day per account, claim and confirm together);
//     502 email_send_failed (nothing stored).
export const AUTH_PLACEHOLDERS_PATH = '/internal/hub/v1/placeholders'
export const AUTH_INVITATIONS_PATH = '/internal/hub/v1/invitations'

export type PreloadContact = { kind: 'email'; value: string } | { kind: 'phone'; value: string }

// What a pre-load can carry, and the ordinary Loyalty command each one is
// sent as. Tickets need rewards.grant; points need rewards.adjust.
export const PRELOAD_COMMANDS = { tickets: 'loyalty.entitlements.grant', points: 'loyalty.points.adjust' } as const
export type PreloadKind = keyof typeof PRELOAD_COMMANDS
export type PreloadCommandType = (typeof PRELOAD_COMMANDS)[PreloadKind]
export const isPreloadCommandType = (value: unknown): value is PreloadCommandType => value === PRELOAD_COMMANDS.tickets || value === PRELOAD_COMMANDS.points

// One request: tickets, points, or both, with one reason. Each part has its
// own commandId (they must differ), so each is its own idempotent command.
// Points are only ever added here (1 to POINTS_ADJUST_LIMIT), sent without
// `expected`: staff are shown no balance, and a credit can't go below zero.
export type PreloadRequest = {
  email?: string
  phone?: string
  name?: string
  reason: string
  invite: boolean
  tickets?: { commandId: string; count: number; expiresOn: string | null }
  points?: { commandId: string; points: number }
}

// Each step reports on its own, so a failure late in the flow never hides
// what earlier steps already did. Running the same request again (same
// commandIds) is safe: Auth answers the same subject, Hub finds the same
// person, and Loyalty answers "duplicate" instead of granting twice.
export type PreloadAccountStep =
  | { status: 'ok'; sub: string; authStatus: AuthAccountStatus }
  | { status: 'failed'; message: string }

export type PreloadPersonStep =
  | { status: 'ok'; id: string; displayName: string; created: boolean }
  | { status: 'failed'; message: string }
  | { status: 'skipped'; message: string }

export type PreloadTicketsResult = { grantId: number; remaining: number }
export type PreloadPointsResult = { ledgerId: number; pointsBalance: number }

export type PreloadGrantStep<R = PreloadTicketsResult> =
  | { outcome: 'applied' | 'duplicate'; result: R }
  // Not sent: the account hasn't proven its email. Hub holds the pre-load
  // until it has, or until expiresAt.
  | { outcome: 'held'; pendingId: string; expiresAt: string }
  // Hub couldn't record the hold, so nothing is held and nothing was sent.
  | { outcome: 'not_held'; message: string }
  | { outcome: 'refused'; code: string; message: string }
  | { outcome: 'unknown' | 'not_configured'; message: string }
  | { outcome: 'skipped'; message: string }

export type InviteKind = 'claim' | 'confirm'

export type PreloadInviteStep =
  | { status: 'sent'; kind: InviteKind; expiresAt: string }
  | { status: 'not_requested' }
  | { status: 'not_needed'; message: string }
  | { status: 'skipped'; message: string }
  | { status: 'already_claimed'; message: string }
  | { status: 'rate_limited'; message: string }
  | { status: 'failed'; message: string }

export type PreloadTicketsPart = { commandId: string; count: number; expiresOn: string | null; grant: PreloadGrantStep<PreloadTicketsResult> }
export type PreloadPointsPart = { commandId: string; points: number; grant: PreloadGrantStep<PreloadPointsResult> }

export type PreloadReport = {
  contact: PreloadContact
  account: PreloadAccountStep
  person: PreloadPersonStep
  // null when the request didn't ask for that part.
  tickets: PreloadTicketsPart | null
  points: PreloadPointsPart | null
  invite: PreloadInviteStep
  // Earlier held pre-loads for this account that this request found proven
  // and sent, other than this request's own.
  releasedPending?: { commandId: string; commandType: PreloadCommandType; outcome: 'applied' | 'duplicate' | 'refused' | 'unknown' | 'not_configured' }[]
}

// One part of a pre-load (its tickets, or its points) held because the
// account's email isn't proven yet. Granted (with its own commandId) once
// BRE Auth says the same subject is an existing_account: at the next
// pre-load for that contact, or on the 30-minute check. Expires after
// PENDING_PRELOAD_DAYS.
export const PENDING_PRELOAD_DAYS = 30
// The 30-minute check signs its core calls as this subject, with no person.
export const PRELOADS_SYSTEM_SUB = 'system:preloads'
export type PendingPreloadStatus = 'pending' | 'granted' | 'refused' | 'expired'
export type PendingPreload = {
  id: string
  commandId: string
  commandType: PreloadCommandType
  personId: string
  contactKind: 'email' | 'phone'
  // Tickets for a ticket hold, points for a points hold.
  amount: number
  ticketExpiresOn: string | null
  reason: string
  heldBy: { personId: string; displayName: string }
  createdAt: string
  expiresAt: string
  // A pending row past expiresAt reads as expired even before the check
  // writes it.
  status: PendingPreloadStatus
  resolvedAt: string | null
  grantOutcome: string | null
  grantMessage: string | null
  lastCheckedAt: string | null
  lastCheckNote: string | null
}

// What the web Worker needs to re-ask Auth and send the command. Only the
// web Worker (as rewards.grant staff or the system check) reads this.
export type PendingPreloadWork = PendingPreload & {
  issuer: string
  subject: string
  contactValue: string
  payload: Record<string, unknown>
  detail: Record<string, unknown>
  heldBy: { personId: string; displayName: string; sub: string }
}

// One row per person on System -> Access. The full grant list lives on the
// person's page.
export type GrantHolder = {
  personId: string
  displayName: string
  primaryEmail: string | null
  status: 'active' | 'inactive'
  capabilities: string[]
  grantCount: number
  // How many of their current grants come from a position (ADR 0071).
  byPositionCount: number
  // The soonest end date among their current grants, if any grant ends.
  earliestValidTo: string | null
  // Their BRE account (active identity link), as their last sign-in said.
  // Shown only; never used to grant anything.
  authAccountId: string | null
  // email_verified from the id token at their last sign-in; null when not
  // read yet (no sign-in since Hub started reading it).
  authEmailVerified: boolean | null
  firstSignedInAt: string | null
}

// "New person": someone with no Hub person yet, set up through the same
// pre-staged account steps as pre-loading, without the rewards. Two places
// offer it, each with its own capability and audit action:
//   governance  the term, committee member and ED dialogs (ADR 0028),
//               governance.manage, `governance.person_created`
//   access      System -> Access, granting Hub access (access.manage),
//               `access.person_created`
// POST /api/people/new {context?, name, email?, phone?, invite?,
// sendConfirm?} takes either capability; `context` says which, and must be
// one the caller holds (left out: governance if they hold it, else access).
// POST /api/governance/people is the same with context governance. BRE Auth
// is asked by email when there is one, else by phone; Hub never matches
// anyone by either. An account whose email Auth hasn't seen proven is
// refused, never linked.
export const NEW_PERSON_CONTEXTS = ['governance', 'access'] as const
export type NewPersonContext = (typeof NEW_PERSON_CONTEXTS)[number]
export const NEW_PERSON_CAPABILITY = { governance: 'governance.manage', access: 'access.manage' } as const satisfies Record<NewPersonContext, string>

// The context a caller may use: the one asked for if they hold its
// capability, else (nothing asked) the first they hold. Null: neither.
export function newPersonContext(held: (capability: (typeof NEW_PERSON_CAPABILITY)[NewPersonContext]) => boolean, asked?: unknown): NewPersonContext | null {
  if (asked !== undefined) return NEW_PERSON_CONTEXTS.includes(asked as NewPersonContext) && held(NEW_PERSON_CAPABILITY[asked as NewPersonContext]) ? (asked as NewPersonContext) : null
  return NEW_PERSON_CONTEXTS.find((ctx) => held(NEW_PERSON_CAPABILITY[ctx])) ?? null
}

export type NewPersonResult =
  | {
      outcome: 'linked'
      person: PersonSummary
      created: boolean
      authStatus: 'existing_account' | 'placeholder_existing' | 'placeholder_created'
      invite: PreloadInviteStep
    }
  | { outcome: 'unconfirmed'; message: string; confirm: PreloadInviteStep }

// The governance name for the same answer (ADR 0028).
export type GovernancePersonResult = NewPersonResult
